import { formatAddress, isReference, resolveId } from '@medplum/core'
import type { Practitioner, Reference } from '@medplum/fhirtypes'
import { referenceDisplay } from '@seen/core/display'
import { ExtendedOrganization } from '@seen/core/models/organizations/ExtendedOrganization'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import { ExtendedPractitionerRole } from '@seen/core/models/practitionerRole'
import { cn } from '@seen/ui'
import {
  IconMail,
  IconMapPin,
  IconPhone,
  IconPrinter,
} from '@tabler/icons-react'
import type { ComponentProps, JSX, ReactNode } from 'react'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { HoverCard } from '#src/components/core/hover-card/HoverCard.tsx'
import { HoverCardContent } from '#src/components/core/hover-card/HoverCardContent.tsx'
import { HoverCardTrigger } from '#src/components/core/hover-card/HoverCardTrigger.tsx'
import { HoverOrganization } from '#src/components/core/hover-card/HoverOrganization.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { DevMedplumAppLinkOverlay } from '#src/components/medplum/DevMedplumAppLinkOverlay.tsx'
import { useRead } from '#src/hooks/useRead.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'

export interface HoverPractitionerProps {
  practitioner: ExtendedPractitioner | Reference<Practitioner> | undefined
  className?: string
  roleInParens?: boolean
}

interface ContactItemProps {
  icon: ReactNode
  value: string | undefined
  label: string
}

function ContactItem({
  icon,
  value,
  label,
}: ContactItemProps): JSX.Element | null {
  if (!value) {
    return null
  }
  return (
    <div className="flex items-start gap-2 text-sm">
      <span className="mt-0.5 text-muted-foreground">{icon}</span>
      <div className="flex min-w-0 flex-col">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="break-all">{value}</span>
      </div>
    </div>
  )
}

export function HoverPractitioner({
  practitioner: practitionerOrReference,
  className,
  roleInParens = false,
}: HoverPractitionerProps): JSX.Element {
  const { data } = useRead(
    ExtendedPractitioner,
    isReference(practitionerOrReference) ? practitionerOrReference : undefined,
    {
      query: {
        enabled: !(practitionerOrReference instanceof ExtendedPractitioner),
      },
    }
  )
  const practitioner =
    practitionerOrReference instanceof ExtendedPractitioner
      ? practitionerOrReference
      : data

  const practitionerReference =
    practitionerOrReference instanceof ExtendedPractitioner
      ? practitionerOrReference.reference
      : practitionerOrReference

  const practitionerId = resolveId(practitionerReference)

  const practitionerName =
    practitioner?.name ?? referenceDisplay(practitionerReference)
  const practitionerHref = practitionerId
    ? `/practitioners/${practitionerId}`
    : undefined

  return (
    <HoverCard openDelay={300} closeDelay={100}>
      <HoverCardTrigger asChild>
        <Link
          to={practitionerHref ?? '#'}
          className={cn(
            'cursor-pointer overflow-hidden text-ellipsis text-nowrap decoration-1 underline-offset-[2.5px] hover:underline',
            className
          )}
          onClick={(e) => {
            if (!practitionerHref) {
              e.preventDefault()
            }
          }}
        >
          {practitionerName}
          {!!practitioner?.rolesInitials && (
            <span
              className={
                roleInParens
                  ? 'font-normal text-foreground-secondary'
                  : 'text-foreground-secondary'
              }
            >
              {roleInParens
                ? ` (${practitioner.rolesInitials})`
                : ` ${practitioner.rolesInitials}`}
            </span>
          )}
        </Link>
      </HoverCardTrigger>
      <HoverCardContent className="w-80" side="bottom" align="start">
        <HoverPractitionerContent
          practitioner={practitioner}
          practitionerReference={practitionerReference}
        />
      </HoverCardContent>
    </HoverCard>
  )
}

export interface HoverPractitionerAvatarProps {
  practitioner: Reference<Practitioner>
  size?: ComponentProps<typeof Avatar>['size']
}

/* Avatar-only trigger variant: same hovercard panel, no inline name.
 * Used in narrow table columns where the column header carries the
 * meaning and only an avatar fits the cell. */
export function HoverPractitionerAvatar({
  practitioner: practitionerReference,
  size = 7,
}: HoverPractitionerAvatarProps): JSX.Element {
  const { data: practitioner } = useRead(
    ExtendedPractitioner,
    practitionerReference
  )
  return (
    <HoverCard openDelay={300} closeDelay={100}>
      <HoverCardTrigger asChild>
        {/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- row onClick is mouse-only; stopPropagation only matters for mouse; trigger wraps a presentational SVG/avatar; HoverCardTrigger handles keyboard. */}
        <span
          className="inline-flex cursor-pointer"
          onClick={(e) => e.stopPropagation()}
        >
          <Avatar profile={practitionerReference} size={size} />
        </span>
      </HoverCardTrigger>
      <HoverCardContent className="w-80" side="bottom" align="start">
        <HoverPractitionerContent
          practitioner={practitioner ?? undefined}
          practitionerReference={practitionerReference}
        />
      </HoverCardContent>
    </HoverCard>
  )
}

export interface HoverPractitionerContentProps {
  practitioner: ExtendedPractitioner | undefined
  practitionerReference: Reference<Practitioner> | undefined
}

export function HoverPractitionerContent({
  practitioner,
  practitionerReference,
}: HoverPractitionerContentProps): JSX.Element {
  const practitionerName =
    practitioner?.name ?? referenceDisplay(practitionerReference)
  const practitionerId = resolveId(practitionerReference)
  const practitionerHref = practitionerId
    ? `/practitioners/${practitionerId}`
    : undefined

  const { data: practitionerRole } = useSearchOne(
    ExtendedPractitionerRole,
    {
      practitioner: practitionerReference?.reference,
      active: true,
      _sort: '-_lastUpdated',
    },
    {
      query: {
        enabled: !!practitionerReference?.reference,
      },
    }
  )

  const { data: organization } = useRead(
    ExtendedOrganization,
    practitionerRole?.organization,
    {
      query: {
        enabled: !!practitionerRole?.organization,
      },
    }
  )

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <Avatar profile={practitionerReference ?? undefined} size="default" />
        <div className="flex min-w-0 flex-col">
          <Link
            to={practitionerHref ?? '#'}
            className="font-medium hover:underline"
            onClick={(e) => {
              if (!practitionerHref) {
                e.preventDefault()
              }
            }}
          >
            {practitionerName}
          </Link>
          <span className="text-sm text-muted-foreground">
            {practitioner?.rolesDisplay ?? 'No role'}
          </span>
          {practitioner && (
            <DevMedplumAppLinkOverlay to={practitioner} inline />
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <ContactItem
          icon={<IconPhone size={14} />}
          value={practitioner?.primaryPhoneNumber}
          label="Phone"
        />
        <ContactItem
          icon={<IconPrinter size={14} />}
          value={practitioner?.primaryFaxNumber}
          label="Fax"
        />
        <ContactItem
          icon={<IconMail size={14} />}
          value={practitioner?.primaryEmail}
          label="Email"
        />
        {practitioner?.primaryAddress && (
          <ContactItem
            icon={<IconMapPin size={14} />}
            value={formatAddress(practitioner.primaryAddress)}
            label="Address"
          />
        )}
      </div>

      <HoverOrganization organization={organization} inline />
    </div>
  )
}
